import { Mapping } from 'src/containers/ProjectDiagram/types/data';
import { getLabelForIri } from 'src/containers/ProjectDiagram/utils/labels';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { getResourceValue } from 'src/containers/ProjectDiagram/utils/resource/createResource';
import {
  AttributeSuggestion,
  ClassSuggestion,
  LabelSuggestion,
  PrimaryIdentifierSuggestion,
  RelationshipSuggestion,
  Suggestion,
  SuggestionType,
} from 'src/containers/ProjectMapping/types';
import { ProjectResource } from 'src/store/state/projects';
import { replaceInvalidIriChars } from 'src/utils/removeInvalidIriChars';

export type IgnoredSuggestion = Suggestion & { mapping?: Mapping };

export const getResourceSuggestionKey = (resourceId: string, value = '') =>
  `${resourceId}__${value}`;

export const getSeparatedSuggestions = (suggestions: Suggestion[]) => {
  const classSuggestions: ClassSuggestion[] = [];
  const primaryIdentifierSuggestions: PrimaryIdentifierSuggestion[] = [];
  const labelSuggestions: LabelSuggestion[] = [];
  const attributeSuggestions: AttributeSuggestion[] = [];
  const relationshipSuggestions: RelationshipSuggestion[] = [];

  suggestions.forEach((suggestion) => {
    if (suggestion.type === SuggestionType.CLASS) {
      classSuggestions.push(suggestion);
    } else if (suggestion.type === SuggestionType.PRIMARY_IDENTIFIER) {
      primaryIdentifierSuggestions.push(suggestion);
    } else if (suggestion.type === SuggestionType.LABEL) {
      labelSuggestions.push(suggestion);
    } else if (suggestion.type === SuggestionType.ATTRIBUTE) {
      attributeSuggestions.push(suggestion);
    } else if (suggestion.type === SuggestionType.RELATIONSHIP) {
      relationshipSuggestions.push(suggestion);
    }
  });

  return {
    classSuggestions,
    primaryIdentifierSuggestions,
    labelSuggestions,
    attributeSuggestions,
    relationshipSuggestions,
  };
};

export const getOrganizedSuggestionRecords = ({
  isClassAutoCreated,
  labelsByIri,
  mappings,
  selectedClasses,
  suggestions,
}: {
  isClassAutoCreated: boolean;
  labelsByIri: Record<string, string>;
  mappings: Mapping[];
  selectedClasses: string[];
  suggestions: Suggestion[];
}) => {
  const classSuggestions: Record<
    string,
    {
      iri: string;
      label: string;
      resource: ProjectResource;
      suggestions: ClassSuggestion[];
    }
  > = {};
  const attributeSuggestions: Record<
    string,
    {
      resource: ProjectResource;
      suggestions: AttributeSuggestion[];
    }
  > = {};
  const relationshipSuggestions: Record<
    string,
    {
      resource: ProjectResource;
      suggestions: RelationshipSuggestion[];
    }
  > = {};
  const labelSuggestions: Record<
    string,
    {
      resource: ProjectResource;
      suggestions: LabelSuggestion[];
    }
  > = {};
  const primaryIdentifierSuggestions: Record<
    string,
    { resource: ProjectResource; suggestions: PrimaryIdentifierSuggestion[] }
  > = {};
  const ignoredSuggestions: IgnoredSuggestion[] = [];

  const separatedSuggestions = getSeparatedSuggestions(suggestions);

  const propertyClassIris = new Set<string>();
  const classLabelsByIri: Record<string, string> = {};
  const addClassLabel = (
    iri: string | undefined,
    label: string | undefined
  ) => {
    if (iri && label) {
      classLabelsByIri[iri] = label;
    }
  };

  if (!isClassAutoCreated) {
    separatedSuggestions.relationshipSuggestions.forEach((suggestion) => {
      if (
        suggestion.propertyType &&
        (selectedClasses.includes(suggestion.classIri) ||
          selectedClasses.includes(suggestion.propertyType))
      ) {
        propertyClassIris.add(suggestion.classIri);
        propertyClassIris.add(suggestion.propertyType);
      }
    });
  } else {
    separatedSuggestions.classSuggestions.forEach((suggestion) => {
      addClassLabel(suggestion.iri, suggestion.label);
    });
    separatedSuggestions.attributeSuggestions.forEach((suggestion) => {
      addClassLabel(suggestion.classIri, suggestion.classLabel);
    });
    separatedSuggestions.relationshipSuggestions.forEach((suggestion) => {
      addClassLabel(suggestion.classIri, suggestion.classLabel);
      addClassLabel(suggestion.propertyType, suggestion.propertyTypeLabel);
    });
  }

  // group classes by original class iri
  separatedSuggestions.classSuggestions.forEach((suggestion) => {
    const { resource } = suggestion;

    // if classes are not part of or connected to the original class selection,
    // do not include the mappings UNLESS the classes are auto-created
    const isMappingIncluded =
      isClassAutoCreated ||
      selectedClasses.includes(suggestion.iri) ||
      propertyClassIris.has(suggestion.iri);
    if (!isMappingIncluded) {
      return;
    }

    // if more than 1 class selected, don't add suggestions
    // for mappings that already exist
    const ignoredClassMapping =
      selectedClasses.length > 1 &&
      mappings.find(
        ({ from, to }) =>
          to &&
          getResourceValue(from) === resource.id &&
          getModelConceptClassValue(to) === suggestion.iri
      );
    if (ignoredClassMapping) {
      ignoredSuggestions.push({ ...suggestion, mapping: ignoredClassMapping });
      return;
    }

    const iriKey = getResourceSuggestionKey(resource.id, suggestion.iri);
    let classIriGroup = classSuggestions[iriKey];
    if (!classIriGroup) {
      // generate a new class for auto-created classes
      classIriGroup = {
        iri: isClassAutoCreated
          ? replaceInvalidIriChars(suggestion.iri)
          : suggestion.iri,
        label:
          (isClassAutoCreated && classLabelsByIri[suggestion.iri]) ||
          getLabelForIri(labelsByIri, suggestion.iri),
        resource,
        suggestions: [],
      };
      classSuggestions[iriKey] = classIriGroup;
    }
    classIriGroup.suggestions.push(suggestion);
  });

  // group relationships by original class iri
  separatedSuggestions.relationshipSuggestions.forEach((suggestion) => {
    const { resource } = suggestion;
    const key = getResourceSuggestionKey(resource.id, suggestion.classIri);
    // do not create property suggestions without a type match suggestion
    if (!classSuggestions[key]) {
      ignoredSuggestions.push(suggestion);
      return;
    }

    let relationshipGroup = relationshipSuggestions[key];
    if (!relationshipGroup) {
      relationshipGroup = { resource, suggestions: [] };
      relationshipSuggestions[key] = relationshipGroup;
    }
    relationshipGroup.suggestions.push({
      ...suggestion,
      iri: isClassAutoCreated
        ? replaceInvalidIriChars(suggestion.iri)
        : suggestion.iri,
      label: getLabelForIri(labelsByIri, suggestion.iri),
      classIri: isClassAutoCreated
        ? replaceInvalidIriChars(suggestion.classIri)
        : suggestion.classIri,
      classLabel:
        (isClassAutoCreated && classLabelsByIri[suggestion.classIri]) ||
        getLabelForIri(labelsByIri, suggestion.classIri),
      propertyType: isClassAutoCreated
        ? replaceInvalidIriChars(suggestion.propertyType)
        : suggestion.propertyType || '',
      propertyTypeLabel:
        (isClassAutoCreated &&
          classLabelsByIri[suggestion.propertyType || '']) ||
        getLabelForIri(labelsByIri, suggestion.propertyType || ''),
    });
  });

  // group attributes by original class iri
  separatedSuggestions.attributeSuggestions.forEach((suggestion) => {
    const { resource, classIri: originalClassIri } = suggestion;
    const key = getResourceSuggestionKey(resource.id, originalClassIri);
    // do not create property suggestions without a type match suggestion
    if (!classSuggestions[key]) {
      ignoredSuggestions.push(suggestion);
      return;
    }

    let attributeGroup = attributeSuggestions[key];
    if (!attributeGroup) {
      attributeGroup = { resource, suggestions: [] };
      attributeSuggestions[key] = attributeGroup;
    }
    attributeGroup.suggestions.push({
      ...suggestion,
      iri: isClassAutoCreated
        ? replaceInvalidIriChars(suggestion.iri)
        : suggestion.iri,
      label: getLabelForIri(labelsByIri, suggestion.iri),
      classIri: isClassAutoCreated
        ? replaceInvalidIriChars(suggestion.classIri)
        : suggestion.classIri,
      classLabel:
        (isClassAutoCreated && classLabelsByIri[suggestion.classIri]) ||
        getLabelForIri(labelsByIri, suggestion.classIri),
    });
  });

  // group primary identifiers by source key
  separatedSuggestions.primaryIdentifierSuggestions.forEach((suggestion) => {
    const { resource, source } = suggestion;
    const key = getResourceSuggestionKey(resource.id, source);

    let primaryIdentifierGroup = primaryIdentifierSuggestions[key];
    if (!primaryIdentifierGroup) {
      primaryIdentifierGroup = { resource, suggestions: [] };
      primaryIdentifierSuggestions[key] = primaryIdentifierGroup;
    }
    primaryIdentifierGroup.suggestions.push(suggestion);
  });

  // group labels by source key
  separatedSuggestions.labelSuggestions.forEach((suggestion) => {
    const { resource, source } = suggestion;
    const key = getResourceSuggestionKey(resource.id, source);

    let labelGroup = labelSuggestions[key];
    if (!labelGroup) {
      labelGroup = { resource, suggestions: [] };
      labelSuggestions[key] = labelGroup;
    }
    labelGroup.suggestions.push(suggestion);
  });

  return {
    classSuggestions,
    attributeSuggestions,
    relationshipSuggestions,
    labelSuggestions,
    primaryIdentifierSuggestions,
    ignoredSuggestions,
  };
};
